
import { computed, defineComponent, onMounted, onUnmounted, ref, watch } from 'vue';
import { Region } from '@/types';
import regionService from '@/services/region-service';

export default defineComponent({
  name: 'RegionSelect',
  props: {
    modelValue: { type: String, default: null },
    disabled: { type: Boolean, default: false },
    invalid: { type: Boolean, default: false },
    selectId: { type: String, default: undefined },
    /** Reload whenever this flips true, so a dialog refreshes each time it opens. */
    active: { type: Boolean, default: true }
  },
  emits: ['update:modelValue', 'update:loading', 'update:failed', 'update:empty', 'regions-loaded', 'blur'],
  setup(props, { emit }) {
    const regions = ref<Region[]>([]);
    const loading = ref(false);
    const failed = ref(false);
    const settled = computed(() => !loading.value && !failed.value);
    const options = computed(() => regions.value.map((region) => ({ value: region.id, label: region.name })));
    const isFixed = computed(() => settled.value && regions.value.length === 1);

    // Only the newest load may touch state; an earlier one resolving late is discarded.
    let generation = 0;
    let unmounted = false;
    onUnmounted(() => {
      unmounted = true;
    });

    const setLoading = (value: boolean) => {
      loading.value = value;
      emit('update:loading', value);
    };

    const setFailed = (value: boolean) => {
      failed.value = value;
      emit('update:failed', value);
    };

    // Parents own their own labels, so they need to know when there is no field worth showing.
    const emitEmpty = () => emit('update:empty', settled.value && regions.value.length === 0);

    const load = async () => {
      generation += 1;
      const attempt = generation;
      setLoading(true);
      setFailed(false);
      emitEmpty();
      try {
        const userRegions = await regionService.getUserRegions();
        if (unmounted || attempt !== generation) return;
        regions.value = userRegions;
        emit('regions-loaded', userRegions);
        // The lone region wins outright: there is no other value the user could pick.
        if (userRegions.length === 1 && userRegions[0].id !== props.modelValue) emit('update:modelValue', userRegions[0].id);
      } catch {
        if (unmounted || attempt !== generation) return;
        regions.value = [];
        setFailed(true);
      } finally {
        if (!unmounted && attempt === generation) {
          setLoading(false);
          emitEmpty();
        }
      }
    };

    const onSelect = (value: string) => emit('update:modelValue', value);

    watch(
      () => props.active,
      (active) => {
        if (active) load();
      }
    );
    onMounted(() => {
      if (props.active) load();
    });

    return { regions, options, loading, failed, isFixed, load, onSelect };
  }
});
